import React, { useCallback, useMemo } from "react";
import { get } from "lodash";
import { useForm, useFormState } from "react-final-form";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";
import RadioInputGroup from "components/Controls/RadioInputGroup";
import HtmlText from "components/HtmlText";
import Input from "rs-components/Input";
import NumberInput from "rs-components/Input/components/NumberInput";
import SearchSelectInput from "rs-components/Input/components/SearchSelectInput";
import { customVariableRelativeValueListPredicate, parameterUnitsItemPredicate, renderParameterUnitsAdditionalListItem, } from "rs-components/Input/components/SearchSelectInput/utils";
import SelectInput from "rs-components/Input/components/SelectInput";
import SelectItemRenderer from "rs-components/Input/components/SelectInput/components/SelectItemRenderer";
import ValueTransformations from "../ValueTransformations";
import { customVariableRelativeValueTypes, customVariableValueTypes, findDictionaryItemByValue, recipeParameterUnitsType, } from "shared/rsDictionary";
import { parameterTypesSelector } from "store/global/global.selectors";
import { isEditableSelector } from "store/knowledge-base-item/knowledge-base-item.selectors";
import { customVariableRelativeParameterOptionsSelector, customVariableRelativeVariableOptionsSelector, getRecipeDialogState, currentRecipeCustomVariablesSelector, } from "store/recipe/recipe.selectors";
import { CustomVariableRelativeValueType, CustomVariableRelativeValueVariableType, CustomVariableValueType, RecipeDialogType, } from "store/recipe/recipe.types";
import { getParameterUnitsOptions } from "store/recipe/recipe.utils";
import { CURRENT_TIME_TRANSFORMATION, getCustomVariableRelativeValueUnitOption, getValueTransformationsEnabled, } from "store/recipe/custom-variables.utils";
import { getFieldRestrictions, getRequestName } from "store/knowledge-base-item/knowledge-base-item.utils";
import { getRecipeCustomVariableFormulaStatus } from "store/recipe/recipe.actions";
import { CustomVariableFormFields } from "../CustomVariableDialog";
import * as styles from "./styles.module.scss";
const ValueFields = ({ valueFieldName, restrictions }) => {
    const baseFieldName = `${CustomVariableFormFields.formula}.${valueFieldName}`;
    const { id: recipeId } = useParams();
    const dispatch = useDispatch();
    const form = useForm();
    const formState = useFormState();
    const { id: variableId } = useSelector(getRecipeDialogState(RecipeDialogType.CUSTOM_VARIABLE));
    const parameterTypes = useSelector(parameterTypesSelector);
    const customVariablesList = useSelector(currentRecipeCustomVariablesSelector);
    const relativeVariableOptions = useSelector(customVariableRelativeVariableOptionsSelector);
    const relativeParameterOptions = useSelector(customVariableRelativeParameterOptionsSelector);
    const editable = useSelector(isEditableSelector);
    const formulaType = useMemo(() => get(formState.values, `${CustomVariableFormFields.formula}.${CustomVariableFormFields.type}`), [formState.values]);
    const isRelativeValue = useMemo(() => get(formState.values, `${baseFieldName}.${CustomVariableFormFields.type}`) ===
        CustomVariableValueType.Relative, [formState.values, baseFieldName]);
    const parameterUnitsOptions = useMemo(() => getParameterUnitsOptions(parameterTypes), [parameterTypes]);
    const selectedRelativeVariableUnit = useMemo(() => get(formState.values, `${baseFieldName}.${CustomVariableFormFields.of}.${CustomVariableFormFields._variableUnit}`), [formState.values, baseFieldName]);
    const onAbsoluteVariableUnitsChange = useCallback((units) => {
        const formula = {
            ...get(formState.values, CustomVariableFormFields.formula),
            [valueFieldName]: {
                ...get(formState.values, baseFieldName),
                [CustomVariableFormFields.units]: units,
            },
        };
        dispatch(getRecipeCustomVariableFormulaStatus({
            recipeId: parseInt(recipeId),
            formula: formula,
        }));
    }, [formState.values, valueFieldName, baseFieldName, dispatch, recipeId]);
    const getUpdatedFormulaStatus = useCallback((updatedOfFields, updatedFormulaFields) => {
        const formula = {
            ...get(formState.values, CustomVariableFormFields.formula),
            [valueFieldName]: {
                ...get(formState.values, baseFieldName),
                ...updatedFormulaFields,
                [CustomVariableFormFields.of]: {
                    ...get(formState.values, `${baseFieldName}.${CustomVariableFormFields.of}`),
                    ...updatedOfFields,
                },
            },
        };
        dispatch(getRecipeCustomVariableFormulaStatus({
            recipeId: parseInt(recipeId),
            formula: formula,
        }));
    }, [formState.values, valueFieldName, baseFieldName, dispatch, recipeId]);
    const onFormulaValueTypeChange = useCallback((value) => {
        const relativeValueSelected = value === CustomVariableValueType.Relative;
        form.batch(() => {
            if (relativeValueSelected) {
                form.change(`${baseFieldName}.${CustomVariableFormFields.transformationType}`, CURRENT_TIME_TRANSFORMATION);
            }
            form.change(`${baseFieldName}`, { [CustomVariableFormFields.type]: value });
        });
    }, [baseFieldName, form]);
    const onValueValueChange = useCallback((value) => {
        const currentValue = isRelativeValue
            ? get(formState.values, `${baseFieldName}.${CustomVariableFormFields.percentage}`)
            : get(formState.values, `${baseFieldName}.${CustomVariableFormFields.value}`);
        const updatedFormulaFields = isRelativeValue
            ? { [CustomVariableFormFields.percentage]: Number(value) }
            : { [CustomVariableFormFields.value]: Number(value) };
        if (!isNaN(Number(value)) && Number(value) !== currentValue) {
            getUpdatedFormulaStatus({}, updatedFormulaFields);
        }
    }, [isRelativeValue, getUpdatedFormulaStatus, formState.values, baseFieldName]);
    const onRelativeVariableChange = useCallback((variable) => {
        const unit = getCustomVariableRelativeValueUnitOption(variable.type, variable.units);
        const isProcessVariable = variable.type === CustomVariableRelativeValueVariableType.ProcessVariable;
        const isCustomVariable = variable.type === CustomVariableRelativeValueVariableType.CustomVariable;
        const isBioreactorParameter = variable.type === CustomVariableRelativeValueVariableType.BioreactorParameter;
        const isRecipeParameter = variable.type === CustomVariableRelativeValueVariableType.RecipeParameter;
        form.batch(() => {
            form.change(`${baseFieldName}.${CustomVariableFormFields.of}.${CustomVariableFormFields.type}`, variable.type);
            form.change(`${baseFieldName}.${CustomVariableFormFields.of}.${CustomVariableFormFields._variableUnit}`, unit?.label);
            form.change(`${baseFieldName}.${CustomVariableFormFields.of}.${CustomVariableFormFields._variableName}`, variable?.value);
            if (isProcessVariable) {
                form.change(`${baseFieldName}.${CustomVariableFormFields.of}.${CustomVariableFormFields.variableType}`, variable.value);
            }
            if (isCustomVariable) {
                form.change(`${baseFieldName}.${CustomVariableFormFields.of}.${CustomVariableFormFields.variableId}`, variable.value);
            }
            if (isBioreactorParameter) {
                form.change(`${baseFieldName}.${CustomVariableFormFields.of}.${CustomVariableFormFields.parameterType}`, variable.value);
            }
            if (isRecipeParameter) {
                form.change(`${baseFieldName}.${CustomVariableFormFields.of}.${CustomVariableFormFields.parameterId}`, variable.value);
            }
        });
        const updatedOfFields = {
            [CustomVariableFormFields.type]: variable.type,
            ...(isProcessVariable ? { [CustomVariableFormFields.variableType]: variable.value } : {}),
            ...(isCustomVariable ? { [CustomVariableFormFields.variableId]: variable.value } : {}),
            ...(isBioreactorParameter ? { [CustomVariableFormFields.parameterType]: variable.value } : {}),
            ...(isRecipeParameter ? { [CustomVariableFormFields.parameterId]: variable.value } : {}),
        };
        getUpdatedFormulaStatus(updatedOfFields);
    }, [form, baseFieldName, getUpdatedFormulaStatus]);
    const renderRelativeValuesFields = useCallback(() => {
        const typeOfRelatedVariable = get(formState.values, `${baseFieldName}.${CustomVariableFormFields.of}.${CustomVariableFormFields._relativeValueType}`);
        const isPercentOfVariable = typeOfRelatedVariable === CustomVariableRelativeValueType.Variable;
        return (React.createElement(React.Fragment, null,
            React.createElement("div", { className: styles.relativeValueInputSuffix }, "% of"),
            React.createElement(Input, { component: SelectInput, name: `${baseFieldName}.${CustomVariableFormFields.of}.${CustomVariableFormFields._relativeValueType}`, itemRenderer: SelectItemRenderer, classes: { component: classNames(styles.select, { [styles.readonly]: !editable }) }, items: customVariableRelativeValueTypes, placeholder: "Select type", valueFormatter: ({ value }) => value, restrictions: { IsNecessary: true }, disableFormChange: true, onChange: (value) => {
                    form.batch(() => {
                        if (value === CustomVariableRelativeValueType.Parameter) {
                            form.change(`${baseFieldName}.${CustomVariableFormFields.cut}`, null);
                            form.change(`${baseFieldName}.${CustomVariableFormFields.transformationType}`, null);
                        }
                        else {
                            form.change(`${baseFieldName}.${CustomVariableFormFields.transformationType}`, CURRENT_TIME_TRANSFORMATION);
                        }
                        setTimeout(() => {
                            form.change(`${baseFieldName}.${CustomVariableFormFields.of}`, null);
                            form.change(`${baseFieldName}.${CustomVariableFormFields.of}`, {
                                [CustomVariableFormFields._relativeValueType]: value,
                                [CustomVariableFormFields.type]: CustomVariableRelativeValueVariableType.Unknown,
                            });
                        }, 0);
                    });
                }, readonly: !editable }),
            !!typeOfRelatedVariable && (React.createElement(React.Fragment, null,
                React.createElement(Input, { component: SearchSelectInput, name: `${baseFieldName}.${CustomVariableFormFields.of}.${CustomVariableFormFields._variableName}`, disableFormChange: true, onChange: onRelativeVariableChange, itemListPredicate: customVariableRelativeValueListPredicate, items: isPercentOfVariable
                        ? relativeVariableOptions.filter(({ value }) => value?.value !== variableId)
                        : relativeParameterOptions, classes: { component: classNames(styles["field-select"], { [styles.readonly]: !editable }) }, placeholder: "Select", readonly: !editable, noResultsRenderer: React.createElement("p", { className: styles.noResults }, `No matching ${isPercentOfVariable ? "variables" : "parameters"}.`), restrictions: {
                        IsNecessary: true,
                    } }),
                !!selectedRelativeVariableUnit && (React.createElement(HtmlText, { text: selectedRelativeVariableUnit?.html ?? selectedRelativeVariableUnit, targetClassName: styles.selectedUnit, targetTagName: "p" }))))));
    }, [
        baseFieldName,
        editable,
        onRelativeVariableChange,
        formState.values,
        relativeVariableOptions,
        relativeParameterOptions,
        selectedRelativeVariableUnit,
        form,
        variableId,
    ]);
    return (React.createElement("div", { className: styles.root },
        React.createElement("div", { className: styles.top },
            React.createElement(Input, { component: RadioInputGroup, name: `${baseFieldName}.${CustomVariableFormFields.type}`, restrictions: {
                    IsNecessary: true,
                    Default: CustomVariableValueType.Absolute,
                }, readonly: !editable, classes: { component: styles.typeInput }, items: customVariableValueTypes, disableFormChange: true, onChange: onFormulaValueTypeChange }),
            React.createElement("div", { className: styles.values },
                React.createElement(Input, { component: NumberInput, name: isRelativeValue
                        ? `${baseFieldName}.${CustomVariableFormFields.percentage}`
                        : `${baseFieldName}.${CustomVariableFormFields.value}`, classes: { component: styles.number }, restrictions: {
                        ...getFieldRestrictions(restrictions, [
                            getRequestName("Formula"),
                            formulaType,
                            valueFieldName,
                            ...(isRelativeValue
                                ? ["Relative", `${CustomVariableFormFields.percentage}`]
                                : ["Absolute", CustomVariableFormFields.value]),
                        ]),
                        IsNumber: true,
                    }, onChange: onValueValueChange, readonly: !editable }),
                React.createElement("div", { className: styles.fields }, isRelativeValue ? (renderRelativeValuesFields()) : (React.createElement(Input, { component: SearchSelectInput, renderAdditionalListItem: renderParameterUnitsAdditionalListItem, itemPredicate: parameterUnitsItemPredicate, useExternalQueryForPredicate: true, isHtmlValueLabel: true, name: `${baseFieldName}.${CustomVariableFormFields.units}`, value: get(formState.values, `${baseFieldName}.${CustomVariableFormFields.units}`)
                        ? findDictionaryItemByValue(recipeParameterUnitsType, get(formState.values, `${baseFieldName}.${CustomVariableFormFields.units}`))
                        : undefined, items: parameterUnitsOptions, classes: { component: classNames(styles["field-select"], { [styles.readonly]: !editable }) }, placeholder: "Select units", readonly: !editable, restrictions: getFieldRestrictions(restrictions, [
                        getRequestName("Formula"),
                        formulaType,
                        valueFieldName,
                        "Absolute",
                        CustomVariableFormFields.units,
                    ]), onChange: onAbsoluteVariableUnitsChange }))))),
        getValueTransformationsEnabled(get(formState.values, baseFieldName), customVariablesList) && (React.createElement(ValueTransformations, { valueFieldName: valueFieldName, restrictions: restrictions }))));
};
export default ValueFields;
